import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, Validators } from '@angular/forms';
import { GisDataModel } from '@bo-client/shared/models/gisData';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import { ValidatorsService } from '@bo-client/shared/forms/validators.service';
import { MetaService } from '@bo-client/core/api/meta-service';
import { distinctUntilChanged, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { IsPersonPipe } from '@bo-client/shared/pipes/salutation.pipe';
import { ContactData } from '@bo-client/shared/models/contactData';

@Component({
  selector: 'app-filiation-form',
  templateUrl: './filiation-form.component.html',
  styleUrls: ['./filiation-form.component.scss']
})
export class FiliationFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() inputFormArray: FormArray;
  @Input() selectedGisData: GisDataModel;
  @Input() id: number;
  @Input() disableContractPartner = false;
  @Input() canUpdate = false;
  public differentContractPartner = false;

  constructor(
    private createFormsService: CreateFormsService,
    public validatorsService: ValidatorsService,
    public metaService: MetaService,
    private isPerson: IsPersonPipe
  ) {
    // Empty
  }

  ngOnInit(): void {
    this.initFormArray();
  }

  private initFormArray() {
    const listFiliation: ContactData[] = (this.inputFormArray as FormArray).value;
    if (!!listFiliation.length) {
      this.differentContractPartner = !!listFiliation[1]?.salutation;
      for (let i = 0; i < listFiliation.length; i++) {
        const salutation = (this.inputFormArray as FormArray).controls[i].get('salutation');
        salutation.setValidators([Validators.required]);
        salutation.markAsTouched();

        this.initSalutationObservable(this.inputFormArray.controls[i] as FormGroup);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.initFormArray();
  }

  onToggle(event: any) {
    this.differentContractPartner = event.checked;
    if (this.differentContractPartner) {
      this.createFiliationData();
    } else {
      this.deleteFiliationData();
    }
  }

  createFiliationData() {
    const newItem: FormGroup = this.createFormsService.createContactDataForm({});
    newItem.get('salutation').setValidators([Validators.required]);
    newItem.get('salutation').markAsTouched();
    this.initSalutationObservable(newItem);
    this.inputFormArray.push(newItem);
  }

  deleteFiliationData() {
    this.inputFormArray.removeAt(1);
  }

  copyAddress() {
    if (this.selectedGisData) {
      this.inputFormArray.controls[0].patchValue({
        street: this.selectedGisData.street,
        streetNo: this.selectedGisData.houseNo,
        streetNoAddition: this.selectedGisData.houseNoAddition,
        uniqueCoId: this.selectedGisData.uniqueCoId,
        zipCode: this.selectedGisData.zipCode,
        city: this.selectedGisData.city
      });
    }
  }

  private initSalutationObservable(formGroup: FormGroup) {
    const salutation = formGroup.controls.salutation;
    salutation.valueChanges
      .pipe(
        takeUntil(this.ngUnsubscribe),
        startWith(salutation.value),
        distinctUntilChanged((a, b) => {
          return a === b || !b;
        }),
        switchMap((value: string) => this.isPerson.transform(value)),
        distinctUntilChanged()
      )
      .subscribe((isPerson) => {
        const { firstName, lastName, company, companyAddition } = formGroup.controls;
        // Manchmal sind die Felder einfach deaktiviert
        // → Wenn man die Salutation ändern kann, sollten die anderen Felder eigentlich auch änderbar sein
        if (salutation.enabled) {
          if (isPerson) {
            firstName.enable();
            lastName.enable();
          } else {
            company.enable();
            companyAddition.enable();
          }
        }
        setValidatorsAndClear(firstName, isPerson);
        setValidatorsAndClear(lastName, isPerson);
        setValidatorsAndClear(company, !isPerson);
        setValidatorsAndClear(companyAddition, false);
      });
  }

  private readonly ngUnsubscribe = new Subject<void>();
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}

const setValidatorsAndClear = (control: AbstractControl, isValidator: boolean) => {
  if (isValidator) {
    control.setValidators([Validators.required]);
    control.updateValueAndValidity();
  } else {
    control.clearValidators();
    control.setValue(null);
  }
};
